<i18n>
{
  "no": {
    "errors": {
      "expired": "Økten har utløpt. Lukk nettleser og forsøk igjen.",
      "ninMismatch": "Vi fant deg ikke i folkeregisteret. Kontakt kundeservice.",
      "signUpLocked": "Noe gikk galt. Lukk nettleser og prøv igjen.",
      "invalidForm": "Alle påkrevde felter må fylles ut.",
      "badState": "Vi kunne ikke fullføre registrering av konto. Vennligst kontakt kundeservice for videre hjelp.",
      "tryAgain": "Vennligst prøv på nytt",
      "contactCustomerService": "Kontakt kundeservice"
    },
    "areYou": "Hei,",
    "confirmUser": "Bekreft personalia",
    "unknownNumber": "Ikke ditt nummer? {0}",
    "unknownPerson": "Ikke deg? {0}",
    "logOut": "Logg ut",
    "notYou": "Ikke deg? Klikk her",
    "firstAndMiddleName": "For- og mellomnavn",
    "surname": "Etternavn",
    "submit": "Bekreft identitet",
    "youAreLoggingInWith": "Du logger inn med Telenor ID med",
    "cancel": "Avbryt",
    "loading": "Laster ...",
    "continueWithoutSignUp": "Fortsett uten fødselsnummer",
    "kyc": {
      "header": "Bekreft din identitet"
    },
    "description": "I henhold til norsk lov må vi verifisere din identitet når du bruker våre tjenester. Om du f.eks. gjør en endring på et abonnement eller handler i nettbutikken, skal du være trygg på at sikkerheten din blir ivaretatt."
  },
  "en": {
    "errors": {
      "expired": "This session has expired. Please close this browser window and try again.",
      "ninMismatch": "We can't find you in the National Registry. Please contact customer service.",
      "signUpLocked": "Something went wrong. Please close this browser window and try again.",
      "invalidForm": "All mandatory fields have to be filled.",
      "badState": "We are unable to complete registration of this account. Please contact customer service.",
      "tryAgain": "Please try again",
      "contactCustomerService": "Please contact customer service"
    },
    "areYou": "Hi,",
    "confirmUser": "Confirm user",
    "unknownNumber": "Not your number? {0}",
    "unknownPerson": "Not you? {0}",
    "logOut": "Log out",
    "notYou": "Not you? Click here",
    "firstAndMiddleName": "First and middle name",
    "surname": "Surname",
    "submit": "Confirm identity",
    "youAreLoggingInWith": "You are logging in with Telenor ID as",
    "cancel": "Cancel",
    "loading": "Loading ...",
    "continueWithoutSignUp": "Continue without national ID Number",
    "kyc": {
      "header": "Verify your identity"
    },
    "description": "According to Norwegian law we need to verify your identity when you are using our services. If you are doing any changes to your subscription or shopping in the online store, you should be confident that your safety is taken care of."
  }
}
</i18n>

<template>
  <div v-if="!isUsingOldSignupFlow" class="page">
    <FormWrapper>
      <div>
        <TnHeading type="h1" size="m" align="center" class="margin-vertical-l">
          {{ t('kyc.header') }}
        </TnHeading>

        <TnNotificationInline
          v-if="error"
          horizontal
          status="critical"
          full-width
          class="margin-bottom-l"
        >
          <TnLink v-if="errorLink" :href="errorLink" :text="t(error)" />
          <span v-else>{{ t(error) }}</span>
        </TnNotificationInline>

        <div v-if="isValidIdps">
          <Idp v-for="idp in idps" :key="idp.displayName" :idp="idp" :url="getIdpUrl(idp)" />

          <UrlForm :on-post="onSubmit">
            <TnButton
              v-if="setupSignUpRequest.canCancelSignUp"
              id="btnContinueWithoutSignup"
              type="submit"
              secondary
              block
              class="margin-top-m"
              :disabled="isLoading"
              :text="t('continueWithoutSignUp')"
            />
          </UrlForm>
        </div>

        <TnParagraph size="s" class="margin-top-l">{{ t('description') }}</TnParagraph>
      </div>
    </FormWrapper>
  </div>

  <div v-else class="page">
    <FormWrapper>
      <div>
        <TnNotificationInline
          v-if="!loadFailed"
          horizontal
          status="success"
          full-width
          class="margin-bottom-l"
        >
          {{
            signUpRequest.signUpId || signUpRequest.msisdn ? t('youAreLoggingInWith') : t('loading')
          }}
          <div><Msisdn :number="signUpRequest.msisdn as string" /></div>
        </TnNotificationInline>

        <TnHeading v-if="!loadFailed" type="h1" size="s" align="center" class="margin-vertical-l">
          {{ isSignUpByPhone ? `${t('areYou')} ${fullName}` : t('confirmUser') }}
        </TnHeading>

        <div v-if="isSignUpByPhone && !loadFailed" class="margin-bottom-m text-center">
          <TnLink
            :to="{ name: 'ibis-sign-up-person', query: { request: signUpRequestId } }"
            :text="t('notYou')"
          />
        </div>

        <TnNotificationInline
          v-if="error"
          horizontal
          status="critical"
          full-width
          class="margin-bottom-l"
        >
          {{ t(error) }}
        </TnNotificationInline>

        <UrlForm v-if="!loadFailed" :on-post="onSubmit">
          <ValidateNumberForm
            v-if="isSignUpByPhone"
            v-model:form-valid="formValid"
            :sign-up-request="signUpRequest"
          />
          <ValidatePersonForm
            v-else
            v-model:form-valid="formValid"
            :sign-up-request="signUpRequest"
          />

          <TnButton
            id="btnSignupSubmit"
            type="submit"
            primary
            block
            class="margin-top-l"
            :disabled="isLoading"
            :text="t('submit')"
          />

          <TnButton
            id="btnSignupCancel"
            secondary
            neutral
            block
            class="margin-top-s"
            :to="{ name: 'ibis-cancel-warning', query: { request: signUpRequestId } }"
            :text="t('cancel')"
          />
        </UrlForm>
      </div>
    </FormWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  TnButton,
  TnHeading,
  TnLink,
  TnNotificationInline,
  TnParagraph,
} from '@telenornorgeinternal/telegrafen-vue3-core';

import FormWrapper from '@/components/FormWrapper.vue';
import Msisdn from '@/components/Msisdn.vue';
import UrlForm from '@/components/UrlForm.vue';
import IdpComponent from '@/components/Idp.vue';
import ValidateNumberForm from '@/views/ibis/sign-up/ValidateNumberForm.vue';
import ValidatePersonForm from '@/views/ibis/sign-up/ValidatePersonForm.vue';
import { SignUpService } from './signup.service';
import { EnvironmentService } from '@/environment';
import { IdpService } from '@/views/ibis/login/idp.service';
import { ErrorService } from '@/error/error.service';
import { RequestParsingService } from '@/views/ibis/requestParsing.service';
import type { ApiError, Idp as IdpModel, SignUpRequestFormFields } from '@/types';

// `include="SignUp"` on <keep-alive> matches this name.
defineOptions({ name: 'SignUp' });

const Idp = IdpComponent;

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const signUpService = new SignUpService();

const signUpRequestId = (route.query.request as string) ?? '';
const errorCode = route.query.error;

const environment = ref<Record<string, string>>({});
const signUpRequest = ref<SignUpRequestFormFields>({});
const setupSignUpRequest = ref<Record<string, unknown>>({});
const idps = ref<IdpModel[]>([]);
const request = ref<Record<string, unknown>>({});
const isValidIdps = ref(false);
const formValid = ref(false);
const fullName = ref('');
const error = ref<string | null>(null);
const errorLink = ref('');
const isLoading = ref(false);
/** True when the initial sign-up request could not be loaded, so only the error is shown. */
const loadFailed = ref(false);

const isSignUpByPhone = computed(() => route.name === 'ibis-sign-up-phone');
const isUsingOldSignupFlow = computed(() => environment.value.useOldSignUpFlow === 'true');

function handleError(apiError: ApiError) {
  error.value = apiError.i18nPath;

  if (apiError.hasLink) {
    errorLink.value = locale.value === 'no' ? environment.value.ksUrlNo : environment.value.ksUrlEn;
  }
}

async function setupBankIdSignUp() {
  const response = (await signUpService.getSetupForSignUp(signUpRequestId)) as unknown as Record<
    string,
    unknown
  >;

  idps.value = new IdpService().getIdps(['no.bankid.vipps']);

  if (errorCode && (response.errorCode === 50000 || !response.errorCode)) {
    handleError(ErrorService.getError('ibis', errorCode));
    return;
  }

  if (response.errorCode) {
    handleError(response as unknown as ApiError);
    return;
  }

  setupSignUpRequest.value = response;
  request.value = RequestParsingService.parseUrlEncodedJson(response.encodedQuery) ?? {};

  if (response.encodedQuery) {
    isValidIdps.value = true;
  }
}

function getIdpUrl(idp: IdpModel) {
  const idpRequest: Record<string, unknown> = { ...request.value };
  idpRequest.authScheme = idp.authScheme;
  idpRequest.idps = null;

  if (!idpRequest.authorizeParams) {
    idpRequest.authorizeParams = {};
  }

  if (idp.amr) {
    console.debug(`Setting AMR to ${idp.amr}`);
    (idpRequest.authorizeParams as Record<string, unknown>).amr = idp.amr;
  }

  return `/sign-up/challenge?requestUrlEncoded=${RequestParsingService.toUrlEncodedJson(idpRequest)}`;
}

async function onSubmit(query: string, event: SubmitEvent) {
  const submitterId = (event.submitter as HTMLElement | null)?.id;

  if (submitterId === 'btnContinueWithoutSignup') {
    isLoading.value = true;
    const response = (await signUpService.cancelSignUp(signUpRequestId)) as unknown as Record<
      string,
      unknown
    >;
    isLoading.value = false;

    if (response?.errorCode) {
      handleError(response as unknown as ApiError);
      formValid.value = true;
    }

    if (response.verificationCodeRequired === false && response.location) {
      window.location.href = response.location as string;
    }

    return;
  }

  if (!formValid.value) {
    error.value = 'errors.invalidForm';
    return;
  }

  error.value = null;
  formValid.value = false;
  isLoading.value = true;
  const response = (await signUpService.sendSignUpRequest(
    signUpRequestId,
    query,
  )) as unknown as Record<string, unknown>;
  isLoading.value = false;

  if (response?.errorCode) {
    handleError(response as unknown as ApiError);
    formValid.value = true;
    return;
  }

  if (response.verificationCodeRequired === false && response.location) {
    window.location.href = response.location as string;
    return;
  }

  await router.push({
    name: 'ibis-sign-up-verify',
    query: { codeId: response.verificationCodeId as string },
  });
}

onMounted(async () => {
  environment.value = await EnvironmentService.getEnvironment();

  if (!isUsingOldSignupFlow.value) {
    await setupBankIdSignUp();
    return;
  }

  const response = (await signUpService.getSignUpRequest(signUpRequestId)) as unknown as Record<
    string,
    unknown
  >;

  if (response.isBankIdSignUp) {
    await setupBankIdSignUp();
    return;
  }

  if (response.errorCode) {
    handleError(response as unknown as ApiError);
    loadFailed.value = true;
    return;
  }

  signUpRequest.value = response as SignUpRequestFormFields;

  if (response.lastName) {
    fullName.value = `${response.firstAndMiddleName} ${response.lastName}`;
  }

  if (!response.lastName && route.name !== 'ibis-sign-up-person') {
    await router.push({
      name: 'ibis-sign-up-person',
      query: { request: signUpRequestId },
    });
  }
});
</script>
